import React from 'react';
import PropTypes from 'prop-types';
import { LuClock, LuDownload, LuExternalLink, LuFileText, LuFolderOpen, LuTrash2 } from 'react-icons/lu';
import Badge from '../../../components/ui/Badge';
import IconButton from '../../../components/ui/IconButton';
import { useTranslation } from '../../../i18n/LanguageContext';
import { downloadFileAs } from '../../../utils/downloadFile';
import { CARD, EmptyState, LoadingRows } from '../components/ProfileUi';

// Dosya boyutunu formatla
export const formatFileSize = (bytes) => {
  if (!bytes) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${Math.round((bytes / k ** i) * 100) / 100} ${sizes[i]}`;
};

/** PDF red, Word blue: the file type at a glance. */
export const FileTypeTile = ({ type, small }) => {
  const pdf = String(type || '').toLowerCase() === 'pdf';
  return (
    <span className={`tw-relative tw-grid tw-shrink-0 tw-place-items-center tw-rounded-xl ${small ? 'tw-h-9 tw-w-9' : 'tw-h-12 tw-w-12'} ${pdf ? 'tw-bg-rose-500/10 tw-text-rose-600 dark:tw-text-rose-300' : 'tw-bg-blue-500/10 tw-text-blue-600 dark:tw-text-blue-300'}`}>
      <LuFileText aria-hidden="true" className={small ? 'tw-h-[18px] tw-w-[18px]' : 'tw-h-6 tw-w-6'} />
      <span className={`tw-absolute -tw-bottom-1 tw-rounded tw-px-1 tw-text-[9px] tw-font-bold tw-uppercase tw-text-white ${pdf ? 'tw-bg-rose-500' : 'tw-bg-blue-500'}`}>
        {pdf ? 'PDF' : String(type || 'DOC').slice(0, 4)}
      </span>
    </span>
  );
};

FileTypeTile.propTypes = { type: PropTypes.string, small: PropTypes.bool };

/** The documents already uploaded for this type, newest first. */
const CvDocumentList = ({ documentType, allowMultiple, documents, loading, highlight, onDelete }) => {
  const { t, locale } = useTranslation();
  const label = (key, vars) => t(`profilePage.cv.${key}`, vars);
  const typeLabel = (key) => t(`profilePage.cv.types.${documentType}.${key}`);
  const formatDate = (value) => (value
    ? new Date(value).toLocaleDateString(locale, { year: 'numeric', month: 'long', day: 'numeric', hour: '2-digit', minute: '2-digit' })
    : '-');

  return (
    <section className={`${CARD} tw-p-4 tw-transition-shadow tw-duration-500 sm:tw-p-5 ${highlight ? 'tw-ring-2 tw-ring-emerald-400/70' : ''}`}>
      <div className="tw-flex tw-items-center tw-justify-between tw-gap-3">
        <h3 className="tw-text-base tw-font-semibold tw-text-slate-900 dark:tw-text-white">{typeLabel('list')}</h3>
        {documents.length > 0 && (
          <Badge tone="success">{allowMultiple ? label('count', { count: documents.length }) : label('uploaded')}</Badge>
        )}
      </div>

      <div className="tw-mt-4">
        {loading ? (
          <LoadingRows count={2} height="tw-h-[4.5rem]" label={label('loading')} />
        ) : documents.length === 0 ? (
          <EmptyState icon={LuFolderOpen} title={typeLabel('empty')} text={label('emptyText')} compact />
        ) : (
          <ul className="tw-space-y-2">
            {documents.map((cv) => (
              <li key={cv.id} className="tw-flex tw-items-center tw-gap-3 tw-rounded-2xl tw-border tw-border-slate-200 tw-bg-white/70 tw-p-3 dark:tw-border-white/10 dark:tw-bg-white/[0.03]">
                <FileTypeTile type={cv.fileType} />
                <div className="tw-min-w-0 tw-flex-1">
                  <p className="tw-truncate tw-text-sm tw-font-semibold tw-text-slate-900 dark:tw-text-white" title={cv.fileName}>{cv.fileName}</p>
                  <p className="tw-mt-0.5 tw-flex tw-flex-wrap tw-items-center tw-gap-x-3 tw-gap-y-0.5 tw-text-xs tw-text-slate-500 dark:tw-text-slate-400">
                    <span className="tw-inline-flex tw-items-center tw-gap-1"><LuClock aria-hidden="true" className="tw-h-3 tw-w-3" />{formatDate(cv.uploadedAt)}</span>
                    <span className="tw-tabular-nums">{formatFileSize(cv.fileSize)}</span>
                  </p>
                </div>
                <div className="tw-flex tw-shrink-0 tw-items-center tw-gap-1">
                  <a
                    href={cv.fileUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={label('actions.view')}
                    aria-label={label('actions.view')}
                    className="tw-grid tw-h-10 tw-w-10 tw-place-items-center tw-rounded-full tw-text-slate-600 tw-transition-colors hover:tw-bg-slate-900/[0.06] hover:tw-text-slate-900 focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-teal-500/50 dark:tw-text-slate-300 dark:hover:tw-bg-white/10 dark:hover:tw-text-white"
                  >
                    <LuExternalLink aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </a>
                  <IconButton label={label('actions.download')} onClick={() => downloadFileAs(cv.fileUrl, cv.fileName)}>
                    <LuDownload aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </IconButton>
                  <IconButton label={label('actions.delete')} tone="alert" onClick={() => onDelete(cv)}>
                    <LuTrash2 aria-hidden="true" className="tw-h-4 tw-w-4" />
                  </IconButton>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </section>
  );
};

CvDocumentList.propTypes = {
  documentType: PropTypes.string.isRequired,
  allowMultiple: PropTypes.bool,
  documents: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  highlight: PropTypes.bool,
  onDelete: PropTypes.func.isRequired,
};

export default CvDocumentList;
